import { Head, useForm } from '@inertiajs/react'
import GuestLayout from '@/layouts/GuestLayout'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { LogInIcon } from 'lucide-react'

interface Props {
  email_hint?: string
  development_login_enabled?: boolean
}

export default function Login({ email_hint, development_login_enabled }: Props) {
  const { data, setData, post, processing } = useForm({
    email: email_hint || '',
    password: '',
  })
  const developmentLoginForm = useForm({ development_login: true })

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    post('/sign_in')
  }

  function handleDevelopmentLogin() {
    developmentLoginForm.post('/sign_in')
  }

  return (
    <>
      <Head title="Login" />

      <Card className="border-0 shadow-none lg:border lg:shadow-sm">
        <CardHeader className="space-y-1 pb-5">
          <CardTitle className="text-xl font-semibold tracking-tight">
            Welcome back
          </CardTitle>
          <CardDescription className="text-[13px]">
            Enter your credentials to access your account
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-[13px] font-medium">
                Email address
              </Label>
              <Input
                id="email"
                type="email"
                value={data.email}
                onChange={(e) => setData('email', e.target.value)}
                placeholder="you@example.com"
                className="h-10"
                autoComplete="email"
                autoFocus
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-[13px] font-medium">
                Password
              </Label>
              <Input
                id="password"
                type="password"
                value={data.password}
                onChange={(e) => setData('password', e.target.value)}
                placeholder="Enter your password"
                className="h-10"
                autoComplete="current-password"
                required
              />
            </div>

            <Button
              type="submit"
              disabled={processing}
              className="w-full h-10 gap-1.5"
            >
              <LogInIcon className="h-4 w-4" />
              {processing ? 'Signing in...' : 'Sign in'}
            </Button>

            {development_login_enabled && (
              <Button
                type="button"
                variant="outline"
                disabled={developmentLoginForm.processing}
                onClick={handleDevelopmentLogin}
                className="h-10 w-full gap-1.5"
              >
                <LogInIcon className="h-4 w-4" />
                {developmentLoginForm.processing ? 'Signing in...' : 'Development login'}
              </Button>
            )}
          </form>
        </CardContent>
      </Card>
    </>
  )
}

Login.layout = (page: React.ReactNode) => <GuestLayout>{page}</GuestLayout>
